import { api } from './api.js';
import { TAG_CONTACT, TAG_CONTACT_DUPLICATES, TAG_CONTACT_MISC, TAG_LIST_CONTACTS } from './tags.js';
import { convertToUrlSearchParams } from '../utils/helper.js';

export const contactEndpoints = api.enhanceEndpoints({ addTagTypes: ['contact'] }).injectEndpoints({
    endpoints: (builder) => ({
        getDashboard: builder.query({
            query: (params) => ({
                url: `/contacts/dashboard/?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_CONTACTS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        addContact: builder.mutation({
            query: (data) => ({
                url: '/contacts/add',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_CONTACTS, TAG_CONTACT, TAG_CONTACT_MISC],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getBusinessCardDetails: builder.mutation({
            query: ({ imageFile }) => {
                const body = new FormData();
                body.append('Content-Type', imageFile.type);
                body.append('image', imageFile);

                return {
                    url: `/contacts/business-card`,
                    method: 'POST',
                    body
                };
            },
            invalidatesTags: [],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getContact: builder.query({
            query: (id) => ({
                url: `/contacts/view/${id}`,
                method: 'GET'
            }),
            providesTags: [TAG_CONTACT],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        editContact: builder.mutation({
            query: ({ id, data }) => ({
                url: `/contacts/edit/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_CONTACTS, TAG_CONTACT, TAG_CONTACT_MISC],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getContactList: builder.query({
            query: (params) => ({
                url: `/contacts?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_CONTACTS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        contactsAdvancedSearch: builder.query({
            query: (params) => ({
                url: `/contacts/search?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_CONTACTS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getCompanies: builder.query({
            query: (params) => ({
                url: `/misc/search-companies?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        deleteContact: builder.mutation({
            query: (id) => ({
                url: `/contacts/delete/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: [TAG_LIST_CONTACTS, TAG_CONTACT_MISC],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        importContacts: builder.mutation({
            query: (data) => ({
                url: '/contacts/import',
                method: 'POST',
                body: data,
                multipart: true
            }),
            invalidatesTags: [TAG_LIST_CONTACTS, TAG_CONTACT_MISC],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        massDeleteContacts: builder.mutation({
            query: (data) => ({
                url: `/contacts/bulk-delete`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_CONTACTS, TAG_CONTACT, TAG_CONTACT_MISC],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        updateContactPicture: builder.mutation({
            query: ({ picture, id }) => {
                const body = new FormData();
                body.append('Content-Type', picture.type);
                body.append('picture', picture);

                return {
                    url: `/contacts/picture/${id}`,
                    method: 'POST',
                    body
                };
            },
            invalidatesTags: [TAG_LIST_CONTACTS, TAG_CONTACT],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getContactsNews: builder.query({
            query: ({ page, per_page, id } = {}) => {
                const queryParams = new URLSearchParams();
                if (page) queryParams.append('page', page);
                if (per_page) queryParams.append('per_page', per_page);

                return {
                    url: `/contacts/news/${id}?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            providesTags: [TAG_CONTACT],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getContactDuplicates: builder.query({
            query: (params) => ({
                url: `/contacts/duplicates?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_CONTACTS, TAG_CONTACT, TAG_CONTACT_DUPLICATES],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getContactDuplicateStatuses: builder.query({
            query: () => ({
                url: '/misc/contact-duplicate-statuses',
                method: 'GET'
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getContactDuplicatesItem: builder.query({
            query: ({ id, contactIds }) => ({
                url: `/contacts/duplicate/${id}/${contactIds}`,
                method: 'GET'
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        updateContactDuplicatesItem: builder.mutation({
            query: ({ id, contactIds, data }) => ({
                url: `/contacts/duplicate/${id}/${contactIds}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_CONTACTS, TAG_CONTACT, TAG_CONTACT_DUPLICATES],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        updateContactDuplicateStatus: builder.mutation({
            query: ({ id, data }) => ({
                url: `/contacts/duplicate/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_CONTACTS, TAG_CONTACT],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        })
    })
});

export const {
    useLazyGetDashboardQuery,
    useAddContactMutation,
    useGetContactQuery,
    useEditContactMutation,
    useLazyGetContactListQuery,
    useLazyContactsAdvancedSearchQuery,
    useLazyGetCompaniesQuery,
    useDeleteContactMutation,
    useImportContactsMutation,
    useMassDeleteContactsMutation,
    useUpdateContactPictureMutation,
    useGetContactsNewsQuery,
    useGetBusinessCardDetailsMutation,
    useGetContactDuplicatesQuery,
    useGetContactDuplicateStatusesQuery,
    useGetContactDuplicatesItemQuery,
    useUpdateContactDuplicatesItemMutation,
    useUpdateContactDuplicateStatusMutation
} = contactEndpoints;
